import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { useEffect, useState } from 'react';

import {
  ColumnField,
  customFieldTypeToTableFormatter,
  showFieldDependingOnProjectViewPreferences,
  TableColumnsButton,
} from '../../../../components/TableColumnsButton';
import { CUSTOM_FIELD_COLUMN_ID_PREFIX } from '../../../../constants';
import { DRIVERS_DEFAULT_TABLE_COLUMNS, DriversTableColumn } from '../../../../context/driver/tableColumns';
import { TableColumn } from '../../../../context/shared/tableColumns';
import { Driver } from '../../../../domain/driver/Driver';
import { useSetDriversColumns } from '../../../../hooks/driver/useSetDriversColumns';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';

type PlanDriversListColumnsButtonProps = {
  id: string;
  disabled?: boolean;
};

export const PlanDriversListColumnsButton = ({ id, disabled }: PlanDriversListColumnsButtonProps) => {
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const { run: setTableColumns, tableColumns } = useSetDriversColumns();
  const [columns, setColumns] = useState<ColumnField[]>([]);

  useEffect(() => {
    const defaultIdentifiers = DRIVERS_DEFAULT_TABLE_COLUMNS.filter((tableColumn) => {
      // Only admins or root users could view the min_time constraint.
      if (tableColumn.identifier !== `min_time`) return true;
      if (userCredentials?.test(`can_manage_backoffice`)) return true;
      return false;
    }).map((tableColumn) => tableColumn.identifier);

    const projectView = activeProject
      ? Object.keys(activeProject?.view.vehicle.getValue()).reduce(
          (amount, key) => ({
            ...amount,
            [key]: (activeProject.view.vehicle as any)[key],
          }),
          {}
        )
      : undefined;

    // Setting all the default column fields (excluding the custom fields).
    const defaultColumns = tableColumns
      .filter(
        (tableColumn: DriversTableColumn) =>
          !tableColumn.identifier.includes(CUSTOM_FIELD_COLUMN_ID_PREFIX) &&
          defaultIdentifiers.includes(tableColumn.identifier) &&
          showFieldDependingOnProjectViewPreferences({
            key: tableColumn.identifier,
            projectView,
          })
      )
      .map((tableColumn: DriversTableColumn) => ({
        id: tableColumn.identifier,
        customField: false,
        intlId: `planoverview.route.table.header.${tableColumn.identifier}`,
        enabled: tableColumn.displayable,
      }));

    // SETTING THE PROJECT CUSTOM FIELDS
    const driverCustomFieldsColumns = (activeProject?.custom_fields?.vehicle ?? [])
      .filter((cF) => cF.enabled)
      .map((field: CustomFieldDefinition) => {
        const customFieldId = `${CUSTOM_FIELD_COLUMN_ID_PREFIX}${field.custom_id}`;

        const tableColumn = tableColumns.find(
          (tableColumn: DriversTableColumn) => tableColumn.identifier === customFieldId
        );
        return {
          id: customFieldId,
          label: `${field.label}`,
          customField: true,
          // Looks into the context data if custom field is stored. Otherwise, initializes it to not enabled.
          enabled: tableColumn?.displayable ?? false,
        };
      });

    setColumns([...defaultColumns, ...driverCustomFieldsColumns]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setColumns, tableColumns]);

  return (
    <TableColumnsButton
      id={id}
      disabled={disabled}
      tableColumns={columns}
      onChange={(columnField: ColumnField, value: boolean) => {
        let columnFound = false;

        const newColumnsObject = tableColumns.map((tableColumn: DriversTableColumn) => {
          if (tableColumn.identifier === columnField.id) {
            columnFound = true;
            return {
              ...tableColumn,
              displayable: value,
            };
          }
          return tableColumn;
        }) as TableColumn<any, Driver>[];

        if (!columnFound) {
          const driverCustomFieldDefinition = (activeProject?.custom_fields?.vehicle ?? []).find(
            (customFieldsDefinition) =>
              customFieldsDefinition.custom_id === columnField.id.replace(CUSTOM_FIELD_COLUMN_ID_PREFIX, ``)
          );
          if (driverCustomFieldDefinition) {
            const type = customFieldTypeToTableFormatter(driverCustomFieldDefinition.field_type);
            newColumnsObject.push({
              identifier: columnField.id,
              type,
              displayable: value,
              filterable: false,
              sortable: type !== `Array`,
              // TODO: CREATE THE SORT FUNCTION FOR CASAES OF TYPE CARTEGORICAL
              // sort?: (a: Driver, b: Driver) => {
              //   return
              // };
            });
          }
        }
        setTableColumns(newColumnsObject);
      }}
    />
  );
};
